// ============================================================================
// hydra/HydraIdSearch.tsx — «Найти человека по номеру HydraID» (SSoT —
// HydraIdService): маска AAA BBB CCC D, локальная проверка контрольной цифры
// (lib/hydraid — зеркало Verhoeff, сервер перепроверяет), при полном номере —
// resolveNumber, иначе searchByNumber (≥3 цифры).
//
// Карточки бывают ЛОКАЛЬНЫЕ и СЕТЕВЫЕ (remote — номер другой базы, найденный в
// адресной книге Хаба): у сетевых нет локальных данных, показываются display и
// бейдж базы-владельца; действие «Написать» открывает межбазовый чат (сервер
// сам резолвит получателя через Хаб — lib/fedChat).
//
// ГДЕ ЖИВЁТ: приложение «Удалённый доступ» (подключение по номеру, как AnyDesk,
// 2026-10-17 — указание владельца). В профиле поиска НЕТ: там только мой номер
// (HydraIdSection). Действия над найденным человеком задаёт ПОТРЕБИТЕЛЬ через
// renderActions (компонент не знает про Remote/чат) — SRP.
// ============================================================================
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { getSDK } from '@/store/sdkStore';
import { display, isComplete, maskInput, normalize, NUMBER_LENGTH } from '@/lib/hydraid';

export interface HydraIdPerson {
  number: string;
  display: string;
  entityId: string;
  login: string;
  name: string;
  avatarUrl: string;
  /** person | bot | persona (волна 1a §4.9): бот/личность — с бейджем, чтобы
   *  номер бота не принимали за человека. */
  subjectType: string;
  /** Номер принадлежит ДРУГОЙ базе (адресная книга сети через Хаб): локальных
   *  данных (entity_id/login/name/avatar_url) у карточки нет — отображение по
   *  display, действие — «Написать» (межбазовый чат). */
  remote: boolean;
  /** Slug базы-владельца номера — только у сетевых (remote) карточек. */
  baseSlug: string;
}

function personOf(value: unknown): HydraIdPerson | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.number !== 'string' || v.number === '') return null;
  return {
    number: v.number,
    display: typeof v.display === 'string' ? v.display : '',
    entityId: typeof v.entityId === 'string' ? v.entityId : '',
    login: typeof v.login === 'string' ? v.login : '',
    name: typeof v.name === 'string' ? v.name : '',
    avatarUrl: typeof v.avatarUrl === 'string' ? v.avatarUrl : '',
    subjectType: typeof v.subjectType === 'string' ? v.subjectType : '',
    remote: v.remote === true,
    baseSlug: typeof v.baseSlug === 'string' ? v.baseSlug : '',
  };
}

export function HydraIdSearch({ renderActions }: { renderActions?: (p: HydraIdPerson) => ReactNode }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HydraIdPerson[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const digits = normalize(query);
  const ready = isComplete(query);

  const search = async () => {
    setSearchError(null);
    setSearched(false);
    if (digits.length < NUMBER_LENGTH && digits.length < 3) {
      setSearchError('Введите минимум 3 цифры номера');
      return;
    }
    if (digits.length === NUMBER_LENGTH && !ready) {
      setSearchError('Неверная контрольная цифра — проверьте номер');
      return;
    }
    setSearching(true);
    try {
      if (ready) {
        const r = await getSDK().hydraId.resolveNumber({ number: digits });
        const person = personOf(r.card);
        setResults(person ? [person] : []);
      } else {
        const r = await getSDK().hydraId.searchByNumber({ query: digits });
        const items = Array.isArray(r.items) ? r.items : [];
        setResults(items.map((it) => personOf(it)).filter((c): c is HydraIdPerson => c !== null));
      }
      setSearched(true);
    } catch (e) {
      setResults([]);
      setSearchError(e instanceof Error ? e.message : 'Не удалось выполнить поиск');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(maskInput(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search();
          }}
          inputMode="numeric"
          placeholder="AAA BBB CCC D"
          className="font-mono tracking-wider"
        />
        <Button size="sm" className="h-8" disabled={searching} onClick={() => void search()}>
          {searching ? 'Поиск…' : 'Найти'}
        </Button>
      </div>
      {digits.length === NUMBER_LENGTH && !ready && (
        <div className="text-[11px] text-amber-600">Контрольная цифра неверна</div>
      )}
      {searchError && <div className="text-xs text-destructive">{searchError}</div>}
      {searched && results.length === 0 && !searchError && (
        <div className="text-xs text-muted-foreground">Никого не найдено</div>
      )}
      {results.map((c) => (
        <div key={c.number} className="flex items-center gap-2 border rounded-lg px-3 py-2">
          <UserAvatar
            uid={c.entityId || c.number}
            fileId={c.avatarUrl}
            name={c.remote ? c.display || display(c.number) : c.name || c.login}
            size={32}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-sm truncate">
                {c.remote ? c.display || display(c.number) : c.name || c.login || c.display}
              </span>
              {c.remote && (
                <span className="shrink-0 rounded border border-primary/30 bg-primary/10 px-1 text-[10px] text-primary">
                  другая база: {c.baseSlug || '—'}
                </span>
              )}
              {c.subjectType === 'bot' && (
                <span className="shrink-0 rounded border border-primary/30 bg-primary/10 px-1 text-[10px] text-primary">бот</span>
              )}
              {c.subjectType === 'persona' && (
                <span className="shrink-0 rounded border border-primary/30 bg-primary/10 px-1 text-[10px] text-primary">личность</span>
              )}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">{c.display || display(c.number)}</div>
          </div>
          {renderActions && (c.entityId || c.remote) && <div className="flex shrink-0 items-center gap-1">{renderActions(c)}</div>}
        </div>
      ))}
    </div>
  );
}
